import { HTTPClient, propsToDatePaged } from "@abaco/web-common/src/HTTP";
import {
    Crop,
    PickedBlock,
    ProseccoBlock, ProseccoFilter,
    SCSData,
    SCSFilters,
    SCSMappedData,
    SCSPlotData
} from "../models/SCSModels";
import {
    PagingParams,
    PagedResults,
} from "@abaco/web-common/src/Paging";
import { FilterPlot, Plot } from "@abaco/portal-global-state/src/modules/global-state/models/Plot";


export default class SupplyChainViewAPI {
    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public async getDataForChart(campaign: number, filters?: {leader?: string; plot?: string}): Promise<SCSPlotData>
    {
        let url = `/sitiagri-rest-api/api_basic/v1/dashboard/scs/campaigns/varieties_by_country?filter=campaign eq ${campaign}`

        if(filters)
        {
            if(filters.leader)
                url += `&filter=scs_subject_id eq ${filters.leader}`

            if(filters.plot)
                url += `&plot_id=${filters.plot}`
        }

        const req = await this.http.get(url)

        return req.data[0]
    }
    public async getDataForContractedChart(campaign: number, filters?: {leader?: string; plot?: string}): Promise<SCSPlotData>
    {
        let url = `/sitiagri-rest-api/api_basic/v1/dashboard/scs/campaigns/varieties_by_country?contracted=true&filter=campaign eq ${campaign}`

        if(filters)
        {
            if(filters.leader)
                url += `&filter=scs_subject_id eq ${filters.leader}`
        }

        const req = await this.http.get(url)

        return req.data[0]
    }

    private static getEqOrLike(key: string)
    {
        switch (key)
        {
            case 'showOnlyBio':
                return null

            case 'is_agreed':
                return 'eq'

            case 'subject_id':
                return 'eq'

            case 'campaign':
                return 'eq'

            case 'plot_id':
                return 'eq'

            case 'rotation':
                return 'eq'

            case 'crop_code':
                return 'eq'

            default:
                return 'like'
        }
    }

    public async getData(leader: number, paging: PagingParams, filters?: SCSFilters): Promise<PagedResults<SCSMappedData>>
    {
        let url = `/sitiagri-rest-api/api_basic/v1/dashboard/scs/plantings?filter=scs_subject_id eq ${leader}`;

        Object.keys(filters ?? []).forEach(key => {
            const el = filters![key as keyof SCSFilters];
            const comparetor = SupplyChainViewAPI.getEqOrLike(key);
            if (!!el && comparetor && comparetor !== "eq"){
                url = url + encodeURI(`&filter=^${key} ${comparetor} ${el?.toString().toUpperCase()}`);
            } else if(comparetor && !!el){
                url = url + `&filter=${key} ${comparetor} ${el}`;
            }
        })

        const req = await this.http.get(url, { params: { count: paging.count, offset: paging.offset } })

        const toRet = req.data.records.map((el: SCSData) => {
            return {
                country: el.country,
                council_label: el.council_label,
                company_code: el.company_code,
                company_name: el.company_name,
                plot_name: el.plot_name,
                block_code: el.block_code,
                is_agreed: el.is_agreed ? 'hco-true' : 'hco-false',
                area_ha: el.area_ha,
                variety: el.variety,
                planting_year: el.planting_year,
                layout: el.layout,
                azimut: el.azimut,
                num_of_plants: el.num_of_plants,
                block_x: el.block_x,
                block_y: el.block_y
            }
        })

        req.data.records = toRet

        return req.data
    }

    public async getCSVData(leader: number, filters?: SCSFilters): Promise<string>
    {
        let url = `/sitiagri-rest-api/api_basic/v1/dashboard/scs/plantings/csv?filter=scs_subject_id eq ${leader}`;

        Object.keys(filters ?? []).forEach(key => {
            const el = filters![key as keyof SCSFilters];
            const comparetor = SupplyChainViewAPI.getEqOrLike(key);
            if (!!el && comparetor && comparetor !== "eq"){
                url = url + encodeURI(`&filter=^${key} ${comparetor} ${el?.toString().toUpperCase()}`);
            } else if(comparetor && !!el){
                url = url + `&filter=${key} ${comparetor} ${el}`;
            }
        })

        const req = await this.http.get(url)
        return req.data;
    }

    public async getCulture(pkCuaa: number, campaign: number, paging: PagingParams, filters?: any): Promise<PagedResults<Crop>>
    {
        const url = `/sitiagri-rest-api/api_basic/v1/plots/subjects/${pkCuaa}/${campaign}/dictionary_crops`

        const params: any = {
            filter: [],
            onlyUsed: 'true'
        }

        if(filters?.crop_label)
            params['filter'].push(`crop_label like ${filters.crop_label.toUpperCase()}`)

        if(filters?.agm_group_code)
            params['filter'].push(`agm_group_code like ${filters.agm_group_code.toUpperCase()}`)

        params['count'] =  String(paging.count)
        params['offset'] =  String(paging.offset)

        const req = await this.http.get(url, { params: params })

        return req.data
    }

    public async pickBlocksCampaign(scs_pk: number, farm_pk: number, campaign_code: string, rotation: number, shape: string): Promise<PickedBlock[]>
    {
        const res = await this.http.get('/siticatasto/dbgis-servlet/tl_data', {
            params: {
                svccode: 'portal/pick_scs_blocks',
                campaign_code,
                rotation,
                shape,
                oid: scs_pk,
                mngbyoid: farm_pk
            }
        })

        return res.data.services[0].entities
    }

    public async getProseccoBlocks(scs_pk: number, block_ids: number[], paging: PagingParams, farm_pk?: number, filters?: Map<string, ProseccoFilter>): Promise<PagedResults<ProseccoBlock>>
    {
        const params: any = {
            count: paging.count,
            offset: paging.offset,
            filter: [],
            extra: "true"
        }

        if(farm_pk)
            params.subject_id = farm_pk

        if(block_ids && block_ids.length > 0)
            params.block_ids = block_ids.join(',')

        filters?.forEach(filter => {
            if(Object.keys(filter).length > 0 && filter.field && filter.value)
                params.filter.push(`^${filter.field} ${filter.filter} ${filter.value }`)
        })

        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/dashboard/prosecco/scs/${scs_pk}/plantings`,
            { params }
        )

        return res.data
    }

    public async getPlots(supplyChainLeaderSubjectId: number | null, farmSubjectId: number | null, campaign: number, withWKT: boolean, paging: PagingParams, filter?: FilterPlot, point?: number[]): Promise<PagedResults<Plot>> {
        const resFilter = [];
        const include_supply_chain = supplyChainLeaderSubjectId ? true : false;
        const primarySubjectId = supplyChainLeaderSubjectId ? supplyChainLeaderSubjectId : farmSubjectId;
        if (filter?.council_label) {
            resFilter.push(`^council_label like ${filter.council_label.toUpperCase()}`)
        }
        if (filter?.plot_name) {
            resFilter.push(`^plot_name like ${filter.plot_name.toUpperCase()}`)
        }
        if (filter?.crop_label) {
            resFilter.push(`^crop_label like ${filter.crop_label.toUpperCase()}`)
        }
        if (filter?.crop_code) {
            resFilter.push(`crop_code like ${filter.crop_code.toUpperCase()}`)
        }
        if (filter?.rotation) {
            resFilter.push(`rotation eq ${filter.rotation}`)
        }
        if (filter?.showOnlyBio) {
            resFilter.push(`fg_bio eq 1`)
        }
        if(filter?.fullTextSearch) {
            resFilter.push(`^full_text_search like ${filter.fullTextSearch.toUpperCase()}`)
        }
        if(supplyChainLeaderSubjectId && farmSubjectId) {
            resFilter.push(`plot_subject_id eq ${farmSubjectId}`)
        }
        const res = await this.http.get<PagedResults<Plot>>(`/sitiagri-rest-api/api_basic/v1/plots/subjects/${primarySubjectId}/${campaign}/plots`, {
            params: {
                include_graphic: withWKT,
                include_supply_chain: include_supply_chain,
                offset: paging.offset,
                count: paging.count,
                filter: resFilter.length ? resFilter : undefined,
                bb_ux: point ? point[0] - 20 : null,
                bb_uy: point ? point[1] - 20 : null,
                bb_lx: point ? point[0] + 20 : null,
                bb_ly: point ? point[1] + 20 : null,
            }
        })
        propsToDatePaged(res.data, ["valid_from"])
        return res.data;
    }
}
